import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService, JoinLinkItem } from 'src/app/auth.service';
import { InputComponent } from 'src/app/shared/components/ui/input/input.component';
import { SelectComponent, SelectOption } from 'src/app/shared/components/ui/select/select.component';
import { GroupsService } from 'src/app/views/groups/groups.service';

export interface JoinLinkDialogData {
  link?: JoinLinkItem;
}

@Component({
  selector: 'app-join-link-dialog',
  template: `
    <div class="standard-dialog">
      <div class="header" mat-dialog-title>
        <div class="title-section">
          <h2>{{ (dialogData?.link ? 'UserManagement.EditJoinLink' : 'UserManagement.CreateJoinLink') | translate }}</h2>
          <p class="subtitle">{{ 'UserManagement.JoinLinkDialogSubtitle' | translate }}</p>
        </div>
        <button mat-icon-button (click)="dialogRef.close()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="form-content custom-scrollbar">
        <form [formGroup]="linkForm" (ngSubmit)="saveLink()">
          <!-- Link Name -->
          <div class="w-full mb-4">
            <mat-label class="form-label mb-1">{{ 'UserManagement.LinkName' | translate }}</mat-label>
            <ui-input formControlName="name" [placeholder]="'UserManagement.LinkNamePlaceholder' | translate" />
          </div>

          <!-- Assign to Group -->
          <div class="w-full mb-4">
            <mat-label class="form-label mb-1">{{ 'UserManagement.AssignToGroup' | translate }}</mat-label>
            <ui-select formControlName="groupId" [options]="groupOptions()" [placeholder]="'UserManagement.AssignToGroup' | translate" />
          </div>

          <!-- Assign Role -->
          <div class="w-full mb-4">
            <mat-label class="form-label mb-1">{{ 'UserManagement.Role' | translate }}</mat-label>
            <ui-select formControlName="role" [options]="roleOptions" [placeholder]="'UserManagement.Role' | translate" />
          </div>

          <!-- Domain Lock -->
          <div class="w-full mb-4">
            <mat-label class="form-label mb-1">{{ 'UserManagement.DomainLockLabel' | translate }}</mat-label>
            <ui-input formControlName="allowedDomain" [placeholder]="'UserManagement.DomainLockPlaceholder' | translate" [prefixIcon]="'at'" />
            <p class="text-xs text-[var(--skz-text-muted)] mt-1.5">{{ 'UserManagement.DomainLockHelpText' | translate }}</p>
          </div>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions class="footer">
        <div class="left-actions"></div>
        <div class="right-actions">
          <button mat-flat-button btnType="neutral" (click)="dialogRef.close()">{{ 'Global.Cancel' | translate }}</button>
          <button mat-flat-button btnType="action" [disabled]="linkForm.invalid || isSaving()" (click)="saveLink()">
            {{ 'Global.Save' | translate }}
          </button>
        </div>
      </mat-dialog-actions>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, TranslateModule, InputComponent, SelectComponent],
})
export class JoinLinkDialogComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private auth = inject(AuthService);
  private groupsService = inject(GroupsService);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  dialogRef = inject<MatDialogRef<JoinLinkDialogComponent>>(MatDialogRef);
  dialogData = inject<JoinLinkDialogData>(MAT_DIALOG_DATA, { optional: true });

  linkForm!: ReturnType<UntypedFormBuilder['group']>;
  groupOptions = signal<SelectOption[]>([]);
  isSaving = signal(false);

  roleOptions: SelectOption[] = [
    { value: 'reader', label: 'Roles.Reader' },
    { value: 'writer', label: 'Roles.Writer' },
    { value: 'admin', label: 'Roles.Admin' }
  ];

  ngOnInit(): void {
    const existing = this.dialogData?.link;
    this.linkForm = this.fb.group({
      name: [existing?.name || '', [Validators.required]],
      groupId: [existing?.groupId || null],
      allowedDomain: [existing?.allowedDomain || ''],
      role: [existing?.role || 'reader', [Validators.required]],
    });

    this.loadGroups();
  }

  loadGroups() {
    this.groupsService.getGroups().subscribe({
      next: (groups: any[]) => {
        const opts: SelectOption[] = [{ value: null, label: 'UserManagement.DefaultGeneralGroup' }, ...(groups || []).map((g) => ({ value: g._id, label: g.name }))];
        this.groupOptions.set(opts);
      },
      error: () => {
        this.groupOptions.set([{ value: null, label: 'UserManagement.DefaultGeneralGroup' }]);
      },
    });
  }

  saveLink() {
    if (this.linkForm.invalid || this.isSaving()) return;

    this.isSaving.set(true);
    const value = this.linkForm.value;
    const payload = {
      name: value.name,
      groupId: value.groupId || null,
      allowedDomain: value.allowedDomain ? value.allowedDomain.trim().toLowerCase().replace(/^@/, '') : null,
      role: value.role || 'reader',
    };

    const existing = this.dialogData?.link;
    const req$ = existing ? this.auth.updateJoinLink(existing._id, payload) : this.auth.createJoinLink(payload);

    req$.subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res.success) {
          this.snackbar.open(this.translate.instant('Snackbar.JoinLinkSaved'), 'Ok', { duration: 2000 });
          this.dialogRef.close(true);
        } else {
          this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
      },
    });
  }
}
